import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CreditCard, Building2, Smartphone, Banknote, ShieldCheck, Lock } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, PHP } from "@/lib/api";
import { securityDepositFor, computeVehiclePrice, locationFeeFor, carwashFeeFor, addonsFor, ADDONS } from "@/lib/policy";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

// QR images uploaded by owner
const QR = {
  paypal: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/erc70vcy_IMG_0216.jpeg",
  bpi: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/ce1t3r0a_IMG_0147.jpeg",
  bdo: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/yl070wwq_IMG_0148.jpeg",
  maya: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/wo97p1jv_IMG_0149.jpeg",
  gcash: "https://customer-assets.emergentagent.com/job_palawan-wheels/artifacts/rl5u8s6n_IMG_0150.jpeg",
};

const MANUAL_METHODS = [
  { id: "gcash", name: "GCash", icon: Smartphone, desc: "Scan the GCash QR with your GCash app", note: "Most popular" },
  { id: "maya", name: "Maya", icon: Smartphone, desc: "Scan the Maya QR via the Maya app", note: "" },
  { id: "bank_transfer", name: "Bank Transfer (BPI / BDO)", icon: Building2, desc: "InstaPay QR or direct transfer to BPI / BDO", note: "InstaPay" },
  { id: "rcbc", name: "Bank Transfer (RCBC)", icon: Building2, desc: "Direct transfer to our RCBC account — details shown after selecting", note: "" },
  { id: "wise", name: "Wise Transfer", icon: CreditCard, desc: "International transfer via Wise — send payment proof for verification", note: "International" },
  { id: "paypal", name: "PayPal", icon: CreditCard, desc: "Scan the PayPal QR or pay via PayPal account", note: "International" },
  { id: "cash", name: "Cash on Pickup", icon: Banknote, desc: "Reserve online, pay the balance in cash at pickup — reservation fee required to secure", note: "" },
];

function QRPanel({ title, accountName, accountInfo, qrs, bgClass }) {
  return (
    <div className={`rounded-xl p-5 bg-gradient-to-br ${bgClass} border`}>
      <div className="flex items-center justify-between mb-4">
        <p className="kicker">{title}</p>
        <span className="text-[10px] uppercase tracking-widest bg-gold-500/15 text-gold-300 px-2 py-0.5 rounded-full">Scan to pay</span>
      </div>
      <p className="text-sm text-foreground/85"><span className="text-gold-300 font-medium">{accountName}</span></p>
      <p className="text-xs text-foreground/60 mt-1">{accountInfo}</p>
      <div className={`mt-4 grid ${qrs.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"} gap-3`}>
        {qrs.map((q) => (
          <div key={q.label} className="bg-white rounded-xl p-3">
            <img src={q.src} alt={q.label} className="w-full h-auto rounded-md" loading="lazy" />
            <p className="text-[10px] uppercase tracking-widest text-ink-900 mt-2 text-center font-medium">{q.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function BankDetailsPanel({ method }) {
  const [details, setDetails] = useState("");
  useEffect(() => {
    api.get(`/payment-instructions/${method}`).then((r) => setDetails(r.data.details || "")).catch(() => setDetails(""));
  }, [method]);
  const title = method === "rcbc" ? "RCBC Bank Transfer" : "Wise Transfer";
  return (
    <div className="rounded-xl p-5 bg-gradient-to-br from-blue-700/15 to-blue-950/10 border border-blue-500/30" data-testid={`${method}-details`}>
      <p className="kicker">{title}</p>
      {details ? (
        <>
          <pre className="mt-3 text-sm text-foreground/85 whitespace-pre-wrap font-sans leading-relaxed">{details}</pre>
          <p className="text-xs text-foreground/55 mt-3">
            After sending, please keep your transfer receipt and send it to us on WhatsApp or email — our team verifies every payment before confirming the booking.
          </p>
        </>
      ) : (
        <p className="mt-3 text-sm text-foreground/70">
          {method === "wise"
            ? "Wise transfer details are provided on request — message us on WhatsApp or email after submitting your reservation and we'll send the Wise account details. Your booking is confirmed once payment is verified."
            : "Details unavailable — please contact us."}
        </p>
      )}
    </div>
  );
}

function PaymentInstruction({ method }) {
  if (method === "rcbc" || method === "wise") return <BankDetailsPanel method={method} />;
  if (method === "gcash") return <QRPanel title="GCash Payment" accountName="Pearly B. (PE***Y B.)" accountInfo="Mobile: 093• ****062" qrs={[{ src: QR.gcash, label: "GCash QR (InstaPay)" }]} bgClass="from-blue-600/15 to-blue-900/10 border-blue-500/30" />;
  if (method === "maya") return <QRPanel title="Maya Payment" accountName="PEARLY BELLONES" accountInfo="@perlietot21 · +63 *** *** 2062" qrs={[{ src: QR.maya, label: "Maya QR (InstaPay)" }]} bgClass="from-green-600/15 to-emerald-900/10 border-green-500/30" />;
  if (method === "bank_transfer") return <QRPanel title="Bank Transfer" accountName="Pearly Bellones" accountInfo="BPI ******634 · BDO ******4629 · InstaPay supported" qrs={[{ src: QR.bpi, label: "BPI Bank QR" }, { src: QR.bdo, label: "BDO Bank QR" }]} bgClass="from-red-600/15 to-yellow-700/10 border-red-500/30" />;
  if (method === "paypal") return <QRPanel title="PayPal Payment" accountName="Pearly Bellones" accountInfo="Conversion fees charged by PayPal are paid by client" qrs={[{ src: QR.paypal, label: "PayPal QR" }]} bgClass="from-blue-800/15 to-blue-950/10 border-blue-600/30" />;
  if (method === "cash") {
    return (
      <div className="rounded-xl p-5 bg-gradient-to-br from-amber-600/10 to-amber-900/10 border border-amber-500/30">
        <p className="kicker">Cash on Pickup</p>
        <p className="text-sm text-foreground/80 mt-3 leading-relaxed">
          Reserve your vehicle online and pay the remaining rental balance in cash at our office when you pick up your vehicle.
          A <span className="text-gold-300">reservation fee is required to secure your booking</span> — send it via GCash, Maya, or Bank transfer (QR shown when selected).
        </p>
      </div>
    );
  }
  return null;
}

export default function Checkout() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [method, setMethod] = useState("paymongo");
  const [paymongoEnabled, setPaymongoEnabled] = useState(false);
  const [policyText, setPolicyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmedPayment, setConfirmedPayment] = useState(false);
  const [scope, setScope] = useState("reservation"); // 'reservation' | 'full'

  useEffect(() => {
    document.title = "Secure Checkout | PB-Lux Vehicle Rental";
    const d = sessionStorage.getItem("pblux_booking_draft");
    const v = sessionStorage.getItem("pblux_vehicle");
    if (!d || !v) {
      navigate("/fleet");
      return;
    }
    setDraft(JSON.parse(d));
    setVehicle(JSON.parse(v));
    api.get("/payments/paymongo/config").then((r) => {
      setPaymongoEnabled(!!r.data.enabled);
      if (!r.data.enabled) setMethod("gcash");
    }).catch(() => setMethod("gcash"));
    api.get("/settings").then((r) => setPolicyText(r.data.cancellation_policy || "")).catch(() => {});
  }, [navigate]);

  if (!draft || !vehicle) return <div className="min-h-screen bg-ink-900"><Navbar /></div>;

  const days = Math.max(1, Math.ceil((new Date(draft.return_date) - new Date(draft.pickup_date)) / (1000 * 60 * 60 * 24)));
  const { fullSubtotal, discountPercent, discountAmount, subtotal, effectiveRate, promo } = computeVehiclePrice(vehicle, days, draft.pickup_date);
  const pickupFee = locationFeeFor(draft.pickup_location_type, vehicle);
  const dropoffFee = locationFeeFor(draft.dropoff_location_type, vehicle);
  const carwashFee = draft.carwash ? carwashFeeFor(vehicle) : 0;
  const selectedAddons = ADDONS.filter((a) => (draft.addons || []).includes(a.id) && a.vehicleType === vehicle.type);
  const addonsFee = selectedAddons.reduce((s, a) => s + a.fee, 0);
  const total = subtotal + pickupFee + dropoffFee + carwashFee + addonsFee;
  const reservationFee = effectiveRate; // one day's rental
  const securityDeposit = draft.collateral_option === "deposit" ? securityDepositFor(vehicle) : 0;
  const amountDueNow = scope === "full" ? total : reservationFee;
  const payableAtPickup = Math.max(0, total - amountDueNow) + securityDeposit;

  const createBooking = async (paymentMethod) => {
    const payload = { ...draft, payment_method: paymentMethod, payment_scope: scope, claims_paid: confirmedPayment };
    const r = await api.post("/bookings", payload);
    return r.data;
  };

  const handleConfirm = async () => {
    if (method !== "paymongo" && method !== "cash" && !confirmedPayment) {
      toast.error("Please confirm you've sent the reservation fee.");
      return;
    }
    try {
      setSubmitting(true);
      if (method === "paymongo") {
        // Open window synchronously (inside the click gesture) so popup blockers allow it
        const payWin = window.open("", "_blank");
        if (payWin) {
          try { payWin.opener = null; payWin.document.write("<p style='font-family:sans-serif;padding:24px'>Loading secure PayMongo checkout…</p>"); } catch {}
        }
        let booking;
        try {
          booking = await createBooking("paymongo");
        } catch (err) {
          if (payWin) payWin.close();
          throw err;
        }
        try {
          const pr = await api.post("/payments/paymongo/checkout", { booking_id: booking.id });
          sessionStorage.removeItem("pblux_booking_draft");
          sessionStorage.removeItem("pblux_vehicle");
          toast.success("Booking saved! Opening secure payment page…");
          // PayMongo blocks iframe embedding — checkout must open in its own tab
          if (payWin) payWin.location.replace(pr.data.checkout_url);
          else window.open(pr.data.checkout_url, "_blank", "noopener,noreferrer");
          navigate(`/booking-success/${booking.id}?via=paymongo`);
          return;
        } catch (pe) {
          if (payWin) payWin.close();
          toast.error(pe?.response?.data?.detail || "Online payment unavailable — please choose a QR transfer method.");
          navigate(`/booking-success/${booking.id}?via=paymongo&canceled=1`);
          return;
        }
      }
      const booking = await createBooking(method);
      sessionStorage.removeItem("pblux_booking_draft");
      sessionStorage.removeItem("pblux_vehicle");
      toast.success("Booking received! Reference: " + booking.booking_ref);
      navigate(`/booking-success/${booking.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const showQRConfirm = method !== "cash" && method !== "paymongo";

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="hero-bg pt-28 sm:pt-32 pb-16 noise" data-testid="checkout-page">
        <div className="max-w-7xl mx-auto px-6">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-gold-300 hover:text-gold-100 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <p className="kicker">Step 2 of 2 — Pay Reservation Fee</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-3">Secure Your Booking</h1>

          <div className="mt-10 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-7">
              {/* Pay reservation fee or in full */}
              <div className="glass-card rounded-2xl p-6" data-testid="payment-scope-section">
                <p className="kicker">How Much Would You Like to Pay Now?</p>
                <RadioGroup value={scope} onValueChange={setScope} className="mt-4 grid sm:grid-cols-2 gap-3">
                  <label className={`flex items-start gap-3 rounded-xl p-4 cursor-pointer border transition ${scope === "reservation" ? "border-gold-500/60 bg-gold-500/5" : "border-gold-500/20"}`}>
                    <RadioGroupItem value="reservation" className="mt-1" data-testid="scope-reservation" />
                    <div>
                      <p className="font-sans-display text-sm text-gold-200">Reservation Fee Only — {PHP(reservationFee)}</p>
                      <p className="text-xs text-foreground/55 mt-1">One day's rental, deducted from your total. Balance due on pickup.</p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 rounded-xl p-4 cursor-pointer border transition ${scope === "full" ? "border-gold-500/60 bg-gold-500/5" : "border-gold-500/20"}`}>
                    <RadioGroupItem value="full" className="mt-1" data-testid="scope-full" />
                    <div>
                      <p className="font-sans-display text-sm text-gold-200">Pay in Full — {PHP(total)}</p>
                      <p className="text-xs text-foreground/55 mt-1">Entire rental charge upfront. {securityDeposit > 0 ? "Security deposit still payable at pickup." : "Nothing left to pay on pickup."}</p>
                    </div>
                  </label>
                </RadioGroup>
              </div>

              {/* Pay online */}
              <div className="glass-card rounded-2xl p-6">
                <p className="kicker">Payment Method</p>
                <RadioGroup value={method} onValueChange={(v) => { setMethod(v); setConfirmedPayment(false); }} className="mt-5 space-y-3">
                  <label
                    data-testid="payment-method-paymongo"
                    className={`flex items-start gap-4 rounded-xl p-5 border transition ${!paymongoEnabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${method === "paymongo" ? "border-gold-500/60 bg-gold-500/5" : "border-gold-500/20 hover:border-gold-500/40"}`}
                  >
                    <RadioGroupItem value="paymongo" className="mt-1" disabled={!paymongoEnabled} />
                    <div className="w-11 h-11 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center flex-shrink-0">
                      <Lock className="w-5 h-5 text-gold-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <p className="font-sans-display text-base text-gold-200">Pay Online Now — Secure Checkout</p>
                        <span className="text-[10px] uppercase tracking-widest bg-green-500/15 text-green-300 px-2 py-0.5 rounded-full">
                          {paymongoEnabled ? "Instant confirmation" : "Coming soon"}
                        </span>
                      </div>
                      <p className="text-xs text-foreground/60 mt-1">
                        Card (Visa/Mastercard) · GCash · Maya · QR Ph — pay {PHP(amountDueNow)} instantly via PayMongo secure checkout.
                      </p>
                    </div>
                  </label>

                  <p className="text-[10px] uppercase tracking-[0.25em] text-foreground/40 pt-2">Or pay via QR transfer</p>
                  {MANUAL_METHODS.map((m) => (
                    <label key={m.id} data-testid={`payment-method-${m.id}`}
                      className={`flex items-start gap-4 rounded-xl p-5 cursor-pointer border transition ${method === m.id ? "border-gold-500/60 bg-gold-500/5" : "border-gold-500/20 hover:border-gold-500/40"}`}>
                      <RadioGroupItem value={m.id} className="mt-1" />
                      <div className="w-11 h-11 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center flex-shrink-0">
                        <m.icon className="w-5 h-5 text-gold-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <p className="font-sans-display text-base text-gold-200">{m.name}</p>
                          {m.note && <span className="text-[10px] uppercase tracking-widest bg-gold-500/15 text-gold-300 px-2 py-0.5 rounded-full">{m.note}</span>}
                        </div>
                        <p className="text-xs text-foreground/60 mt-1">{m.desc}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              {method !== "paymongo" && (
                <div data-testid="payment-instructions">
                  <PaymentInstruction method={method} />
                </div>
              )}

              {showQRConfirm && (
                <label className="glass-card rounded-xl p-5 flex items-center gap-3 cursor-pointer hover:border-gold-500/40">
                  <input type="checkbox" checked={confirmedPayment} onChange={(e) => setConfirmedPayment(e.target.checked)} data-testid="confirmed-payment-checkbox" className="w-5 h-5 accent-gold-500" />
                  <span className="text-sm text-foreground/85">
                    I've sent <span className="text-gold-300 font-medium">{PHP(amountDueNow)}</span> using the details above. I'll share the receipt with the PB-Lux team for verification.
                  </span>
                </label>
              )}

              <div className="glass-card rounded-2xl p-6 text-sm text-foreground/75 leading-relaxed">
                <p className="kicker">Good to Know</p>
                <ul className="mt-4 space-y-2.5 list-disc list-inside marker:text-gold-500">
                  <li>Booking confirmation goes to <span className="text-gold-300">{draft.customer_email}</span></li>
                  <li>Reservation fee = <span className="text-gold-200">one day's rental ({PHP(reservationFee)})</span>, fully deducted from your total</li>
                  <li>Bring your <span className="text-gold-200">Driver's License + 1 valid ID</span> on pickup</li>
                  {securityDeposit > 0 ? (
                    <li>Refundable security deposit on pickup: <span className="text-gold-200">{PHP(securityDeposit)}</span> (refunded after return & inspection)</li>
                  ) : (
                    <li>Security: <span className="text-gold-200">passport on pickup</span>, subject to verification & approval (no cash deposit)</li>
                  )}
                  {(pickupFee > 0 || dropoffFee > 0) && (
                    <li>Location fees shown separately below — your daily rate never changes by location</li>
                  )}
                </ul>
                {policyText && (
                  <div className="mt-5 flex items-start gap-3 p-4 bg-royal-700/20 rounded-lg" data-testid="cancellation-policy">
                    <ShieldCheck className="w-5 h-5 text-gold-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-foreground/70"><span className="text-gold-200 font-medium">Cancellation / Refund: </span>{policyText}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="lg:col-span-4">
              <div className="sticky top-28 glass-card rounded-2xl p-6">
                <div className="flex gap-4">
                  <img src={vehicle.image} alt={vehicle.name} className="w-24 h-24 object-cover rounded-xl" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-gold-500/80">{vehicle.brand}</p>
                    <p className="font-display text-xl text-foreground">{vehicle.model}</p>
                    <p className="text-xs text-foreground/55 mt-1">{days} {days === 1 ? "day" : "days"}</p>
                  </div>
                </div>
                <div className="gold-divider my-5" />
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between text-foreground/75">
                    <span>{PHP(effectiveRate)} × {days}</span>
                    <span>{PHP(fullSubtotal)}</span>
                  </div>
                  {promo && (
                    <div className="flex justify-between text-green-300" data-testid="checkout-promo">
                      <span>{vehicle.promo_label || "Promo rate"}</span>
                      <span className="line-through text-foreground/40">{PHP(vehicle.daily_rate)}/day</span>
                    </div>
                  )}
                  {discountPercent > 0 && (
                    <div className="flex justify-between text-green-300" data-testid="checkout-discount">
                      <span>{discountPercent}% duration discount</span>
                      <span>− {PHP(discountAmount)}</span>
                    </div>
                  )}
                  {pickupFee > 0 && (
                    <div className="flex justify-between text-foreground/75" data-testid="checkout-pickup-fee">
                      <span>{draft.pickup_location_type === "el_nido" ? "El Nido pickup" : "Delivery"}</span>
                      <span>{PHP(pickupFee)}</span>
                    </div>
                  )}
                  {dropoffFee > 0 && (
                    <div className="flex justify-between text-foreground/75" data-testid="checkout-dropoff-fee">
                      <span>{draft.dropoff_location_type === "el_nido" ? "El Nido drop-off" : "Hotel collection"}</span>
                      <span>{PHP(dropoffFee)}</span>
                    </div>
                  )}
                  {selectedAddons.map((a) => (
                    <div key={a.id} className="flex justify-between text-foreground/75" data-testid={`checkout-addon-${a.id}`}>
                      <span>{a.label}</span>
                      <span>{PHP(a.fee)}</span>
                    </div>
                  ))}
                  {carwashFee > 0 && (
                    <div className="flex justify-between text-foreground/75">
                      <span>Car wash</span>
                      <span>{PHP(carwashFee)}</span>
                    </div>
                  )}
                  {securityDeposit > 0 ? (
                    <div className="flex justify-between text-blue-300">
                      <span>+ Refundable deposit (pickup)</span>
                      <span>{PHP(securityDeposit)}</span>
                    </div>
                  ) : (
                    <div className="flex justify-between text-blue-300" data-testid="checkout-collateral-id">
                      <span>Security option</span>
                      <span>Passport</span>
                    </div>
                  )}
                </div>
                <div className="gold-divider my-5" />
                <div className="space-y-2.5">
                  <div className="flex justify-between items-end">
                    <span className="text-xs uppercase tracking-widest text-foreground/55">Total Rental</span>
                    <span className="font-display text-3xl text-gold-300" data-testid="checkout-total">{PHP(total)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gold-200 bg-gold-500/10 rounded-lg px-3 py-2" data-testid="checkout-due-now">
                    <span>{scope === "full" ? "Full payment — due now" : "Reservation fee — due now"}</span>
                    <span className="font-medium">{PHP(amountDueNow)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-foreground/70" data-testid="checkout-payable-pickup">
                    <span>Payable at pickup{securityDeposit > 0 ? " (incl. deposit)" : ""}</span>
                    <span>{PHP(payableAtPickup)}</span>
                  </div>
                </div>
                <button onClick={handleConfirm} disabled={submitting} className="btn-gold w-full mt-6 disabled:opacity-60" data-testid="confirm-booking">
                  {submitting ? "Processing…" : method === "paymongo"
                    ? (<>Pay {PHP(amountDueNow)} Securely <Lock className="w-4 h-4" /></>)
                    : (<>Confirm Booking <ArrowRight className="w-4 h-4" /></>)}
                </button>
                <Link to="/fleet" className="block text-center text-xs text-foreground/50 hover:text-gold-300 mt-4">Cancel and return to fleet</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
